// ── Recurring Expenses — period math + generator ───────────────────────────
//
// A RecurringExpense is just a template (name/amount/day-of-month). It never
// appears in the Expenses list or GL itself — each due calendar period gets
// materialized into a normal unpaid Expense doc, written the exact same way
// ExpenseModal's own unpaid create-branch writes one (Expenses.tsx), so the
// rest of the app (PayModal, currency conversion, GL) needs no changes.

import {
  collection, doc, runTransaction, serverTimestamp,
  type Firestore,
} from 'firebase/firestore';
import { buildEntry, postEntry, entryId, linesExpense } from './ledger';
import type { RecurringExpense, Business, User } from '../types';

function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

/** "YYYY-MM-DD" → "YYYY-MM" */
export function periodOf(dateISO: string): string {
  return dateISO.slice(0, 7);
}

export function currentPeriod(): string {
  return periodOf(new Date().toISOString().slice(0, 10));
}

/** "YYYY-MM" shifted by n months (n may be negative). */
export function addMonths(period: string, n: number): string {
  const [y, m] = period.split('-').map(Number);
  const total = y * 12 + (m - 1) + n;
  const ny = Math.floor(total / 12);
  const nm = total - ny * 12 + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

/** The due date for a given period, clamping dayOfMonth to that period's
 *  actual last day (e.g. 31 in February → 28 or 29). */
export function dueDateForPeriod(period: string, dayOfMonth: number): string {
  const [y, m] = period.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate(); // day 0 of next month = last day of this one
  const day = Math.min(Math.max(1, dayOfMonth), lastDay);
  return `${period}-${String(day).padStart(2, '0')}`;
}

const MAX_CATCHUP_PERIODS = 36;

/** Every period from the template's floor (its own start, or the period
 *  right after the last one generated) through `uptoPeriod` inclusive.
 *  Empty once caught up. Capped so a corrupt/ancient lastGeneratedPeriod
 *  can never generate an unbounded backlog. */
export function periodsDue(tpl: RecurringExpense, uptoPeriod: string): string[] {
  const floor = tpl.lastGeneratedPeriod ? addMonths(tpl.lastGeneratedPeriod, 1) : tpl.startPeriod;
  const start = floor < tpl.startPeriod ? tpl.startPeriod : floor;
  if (start > uptoPeriod) return [];
  const periods: string[] = [];
  let p = start;
  while (p <= uptoPeriod && periods.length < MAX_CATCHUP_PERIODS) {
    periods.push(p);
    p = addMonths(p, 1);
  }
  return periods;
}

/**
 * Generates any Expense rows due for every active template, one
 * runTransaction per template. Re-reads the template inside the transaction
 * before writing, so two tabs/sessions racing to generate the same period
 * can't double-create it — whichever commits first advances
 * lastGeneratedPeriod, and a racing retry then sees nothing left to do.
 * Returns the total number of Expense rows created.
 */
export async function generateDueExpenses(
  db: Firestore,
  business: Business,
  user: User,
  templates: RecurringExpense[],
): Promise<number> {
  const upto = currentPeriod();
  let created = 0;

  for (const tpl of templates) {
    if (!tpl.isActive) continue;
    if (periodsDue(tpl, upto).length === 0) continue; // cheap skip before opening a transaction

    const tplRef = doc(db, 'recurringExpenses', tpl.id);
    created += await runTransaction(db, async tx => {
      const tplSnap = await tx.get(tplRef);
      if (!tplSnap.exists()) return 0;
      const live = { id: tpl.id, ...tplSnap.data() } as RecurringExpense;
      const due = periodsDue(live, upto);
      if (due.length === 0) return 0;

      for (const period of due) {
        const expRef = doc(collection(db, 'expenses'));
        const dueDate = dueDateForPeriod(period, live.dayOfMonth);
        tx.set(expRef, stripUndef({
          businessId: business.id,
          category: live.category,
          vendorEntityId: live.vendorEntityId || undefined,
          description: live.description,
          amountUSD: live.amount,
          currency: live.currency,
          date: dueDate,
          dueDate,
          paymentStatus: 'unpaid',
          paidUSD: 0,
          notes: live.notes || undefined,
          recurringExpenseId: live.id,
          recurringPeriod: period,
          preferredBankAccountId: live.defaultBankAccountId || undefined,
          createdAt: serverTimestamp(), updatedAt: serverTimestamp(), createdBy: user.id,
        }));
        postEntry(tx, db, buildEntry({
          businessId: business.id, sourceType: 'expense', sourceId: expRef.id,
          sourceCollection: 'expenses', date: dueDate, currency: live.currency,
          createdBy: user.id, note: live.description,
          lines: linesExpense(live.amount, live.category, 'accrued', live.vendorEntityId),
        }), entryId('expense', expRef.id));
      }

      tx.update(tplRef, { lastGeneratedPeriod: upto, updatedAt: serverTimestamp() });
      return due.length;
    });
  }

  return created;
}
